import { getProcedureDateLabel, getProcedurePhraseForPatientCopy } from "@/features/diaries/utils/displayLabels";
import type { ProcedureEventKind } from "@/features/diaries/utils/getProcedureInformationFromDiary";
import { pickAppCopy, type SupportedLocale } from "@patient-watch/i18n";
import { useMemo } from "react";
import { useAppLocale } from "./LocaleProvider";
import { setupCopyEn } from "@patient-watch/i18n/webapp/en-GB/setupCopy";
import { setupCopyDe } from "@patient-watch/i18n/webapp/de-DE/setupCopy";
import { setupCopyDa } from "@patient-watch/i18n/webapp/da-DK/setupCopy";
import { setupCopyEnUs } from "@patient-watch/i18n/webapp/en-US/setupCopy";
import { setupCopyFr } from "@patient-watch/i18n/webapp/fr-FR/setupCopy";
import { setupCopyIt } from "@patient-watch/i18n/webapp/it-IT/setupCopy";
import { setupCopyEs } from "@patient-watch/i18n/webapp/es-ES/setupCopy";

interface SetupCopyBase {
    yourHospitalNumber: string;
    hospitalNumberIntro: string;
    hospitalNumberFieldHint: string;
    dontKnow: string;
    skipForNow: string;
    whichSideAffectedTitle: string;
    sideSetupLeadInTrackersOnly: string;
    sideSetupLeadInProceduresOnly: string;
    sideSetupLeadInBoth: string;
    sideSetupBothSidesNote: string;
    saveDate: string;
    pickDate: string;
    clearDateAndTime: string;
    missingReferenceDateDataError: string;
    referenceDateMinMessage: string;
    referenceDateMaxMessage: string;
    referenceDateQuestion: (kind: ProcedureEventKind) => string;
    referenceDateDescriptionFor: (phrase: string, kind: ProcedureEventKind) => string;
    productStartDateLabel: (phrase: string) => string;
    chooseDateError: (phrase: string) => string;
    chooseProductStartDateError: (phrase: string) => string;
    savedProcedureDateToast: (phrase: string) => string;
    savedProductStartDateToast: (phrase: string) => string;
}

interface SetupCopy extends SetupCopyBase {
    nameListSeparator: (index: number, total: number) => string;
    joinLabels: (labels: string[]) => string;
    getReferenceDateSetupCopy: (
        procedureHeading: string | null | undefined,
        kind?: ProcedureEventKind
    ) => ReferenceDateSetupCopy;
}

export interface ReferenceDateSetupCopy {
    title: string;
    description: string;
    fieldLabel: string;
    chooseDateError: string;
    minMessage: string;
    maxMessage: string;
    savedToast: string;
    saveButton: string;
}

const setupCopyBase: Record<SupportedLocale, SetupCopyBase> = {
    "en-GB": setupCopyEn,
    "en-US": setupCopyEnUs,
    "de-DE": setupCopyDe,
    "da-DK": setupCopyDa,
    "fr-FR": setupCopyFr,
    "it-IT": setupCopyIt,
    "es-ES": setupCopyEs,
};

function inferProcedureEventKind(heading: string): ProcedureEventKind {
    const key = heading.trim().toLowerCase();
    if (
        !key ||
        key === "procedure" ||
        key === "behandlung" ||
        key === "behandling" ||
        key === "intervention" ||
        key === "procedura" ||
        key === "procedimiento"
    ) {
        return "procedure";
    }
    if (key === "injection" || key === "injektion" || key === "iniezione" || key === "inyección") return "injection";
    if (
        key.includes("shockwave") ||
        key.includes("stødbølge") ||
        key.includes("stoßwelle") ||
        key.includes("ondes de choc") ||
        key.includes("onde de choc") ||
        key.includes("onde d'urto") ||
        key.includes("onda d'urto") ||
        key.includes("ondas de choque") ||
        key.includes("onda de choque")
    ) {
        return "shockwave";
    }
    if (
        key === "surgery" ||
        key === "operation" ||
        key === "opération" ||
        key === "chirurgie" ||
        key === "intervento" ||
        key === "intervención" ||
        key === "cirugía"
    ) {
        return "surgery";
    }
    if (
        key === "consultation" ||
        key === "beratung" ||
        key === "konsultation" ||
        key === "consulto" ||
        key === "consulta"
    ) {
        return "consultation";
    }
    if (key === "product" || key === "produkt" || key === "produit" || key === "prodotto" || key === "producto")
        return "product";
    // Named surgeries / interventions keep "when is your …" wording.
    return "surgery";
}

function buildSetupCopy(locale: SupportedLocale): SetupCopy {
    const copy = setupCopyBase[locale] ?? setupCopyBase["en-GB"];
    const andWord =
        locale === "de-DE"
            ? " und "
            : locale === "da-DK"
              ? " og "
              : locale === "fr-FR"
                ? " et "
                : locale === "it-IT"
                  ? " e "
                  : locale === "es-ES"
                    ? " y "
                    : " and ";

    return {
        ...copy,
        nameListSeparator: (index: number, total: number) => {
            if (index === 0) return "";
            if (index === total - 1) {
                if (total === 2) return andWord;
                return locale === "en-GB" || locale === "en-US" ? ", and " : andWord;
            }
            return ", ";
        },
        joinLabels: (labels: string[]) => {
            if (labels.length <= 1) return labels[0] ?? "";
            if (labels.length === 2) return `${labels[0]}${andWord}${labels[1]}`;
            if (locale === "en-GB" || locale === "en-US") {
                return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
            }
            return `${labels.slice(0, -1).join(", ")}${andWord}${labels[labels.length - 1]}`;
        },
        getReferenceDateSetupCopy: (
            procedureHeading: string | null | undefined,
            kind?: ProcedureEventKind
        ): ReferenceDateSetupCopy => {
            const heading = procedureHeading?.trim() || "Procedure";
            const resolvedKind = kind ?? inferProcedureEventKind(heading);
            const phrase =
                resolvedKind === "product"
                    ? heading === "Procedure"
                        ? (PROCEDURE_PRODUCT_FALLBACK[locale] ?? "product")
                        : heading
                    : getProcedurePhraseForPatientCopy(heading, locale);

            return {
                title: copy.referenceDateQuestion(resolvedKind),
                description: copy.referenceDateDescriptionFor(phrase, resolvedKind),
                fieldLabel:
                    resolvedKind === "product"
                        ? copy.productStartDateLabel(phrase)
                        : getProcedureDateLabel(heading, locale),
                chooseDateError:
                    resolvedKind === "product"
                        ? copy.chooseProductStartDateError(phrase)
                        : copy.chooseDateError(phrase),
                minMessage: copy.referenceDateMinMessage,
                maxMessage: copy.referenceDateMaxMessage,
                savedToast:
                    resolvedKind === "product"
                        ? copy.savedProductStartDateToast(phrase)
                        : copy.savedProcedureDateToast(phrase),
                saveButton: copy.saveDate,
            };
        },
    };
}

const PROCEDURE_PRODUCT_FALLBACK: Partial<Record<SupportedLocale, string>> = {
    "en-GB": "product",
    "de-DE": "Produkt",
    "da-DK": "produkt",
    "fr-FR": "produit",
    "it-IT": "prodotto",
    "es-ES": "producto",
};

const setupCopyByLocale: Record<SupportedLocale, SetupCopy> = {
    "en-GB": buildSetupCopy("en-GB"),
    "en-US": buildSetupCopy("en-US"),
    "de-DE": buildSetupCopy("de-DE"),
    "da-DK": buildSetupCopy("da-DK"),
    "fr-FR": buildSetupCopy("fr-FR"),
    "it-IT": buildSetupCopy("it-IT"),
    "es-ES": buildSetupCopy("es-ES"),
};

export function useSetupCopy(): SetupCopy {
    const { locale } = useAppLocale();
    return useMemo(() => pickAppCopy(setupCopyByLocale, locale), [locale]);
}

export function getSetupCopy(locale: SupportedLocale): SetupCopy {
    return pickAppCopy(setupCopyByLocale, locale);
}
